import { useEffect, useRef } from "react";
import { invalidateDbCache } from "../db/engine";
import {
  JOURNEY_EVENT,
  JOURNEY_LIVE_INTERVAL_MS,
  isJourneyStorageKey,
} from "../db/live";

const listeners = new Set();
let started = false;
let intervalId = null;
let debounceId = null;
let pendingInvalidate = false;

function flush() {
  debounceId = null;
  if (pendingInvalidate) {
    invalidateDbCache();
    pendingInvalidate = false;
  }
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      /* keep the bus alive */
    }
  });
}

function schedule(invalidate) {
  if (invalidate) pendingInvalidate = true;
  if (debounceId) clearTimeout(debounceId);
  debounceId = setTimeout(flush, 80);
}

function onStorage(event) {
  if (!isJourneyStorageKey(event.key)) return;
  schedule(true);
}

function onSameTab() {
  schedule(false);
}

function onVisible() {
  if (typeof document !== "undefined" && document.hidden) return;
  schedule(true);
}

function startBus() {
  if (started || typeof window === "undefined") return;
  started = true;
  intervalId = window.setInterval(() => {
    if (typeof document !== "undefined" && document.hidden) return;
    schedule(true);
  }, JOURNEY_LIVE_INTERVAL_MS);
  window.addEventListener("storage", onStorage);
  window.addEventListener(JOURNEY_EVENT, onSameTab);
  document.addEventListener("visibilitychange", onVisible);
  window.addEventListener("focus", onVisible);
}

function stopBus() {
  if (!started) return;
  started = false;
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
  if (debounceId) {
    clearTimeout(debounceId);
    debounceId = null;
  }
  window.removeEventListener("storage", onStorage);
  window.removeEventListener(JOURNEY_EVENT, onSameTab);
  document.removeEventListener("visibilitychange", onVisible);
  window.removeEventListener("focus", onVisible);
}

export function subscribeJourneyLive(listener) {
  listeners.add(listener);
  startBus();
  return () => {
    listeners.delete(listener);
    if (!listeners.size) stopBus();
  };
}

/**
 * Reusable live feed: 3s poll while the tab is visible, plus storage events
 * (other tabs) and a same-tab journey event after each write. Swap the poll
 * for a server stream later without changing callers.
 */
export function useJourneyLive(onChange) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    return subscribeJourneyLive(() => {
      onChangeRef.current?.();
    });
  }, []);
}
